import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, Button, Table, Form, Alert, Spinner } from 'react-bootstrap';
import useAuth from '../../pages/useAuth';

const StatCard = ({ title, value, color = '#fff', active, onClick }) => (
     <Card onClick={onClick} style={{
          cursor: onClick ? 'pointer' : 'default',
          borderRadius: 8,
          border: active ? '2px solid #4f46e5' : '1px solid #eee',
          padding: 8,
          boxShadow: 'none',
          background: color
     }}>
          <Card.Body style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 8 }}>
               <div style={{ lineHeight: 1 }}>
                    <div style={{ fontSize: 18, color: '#666' }}>{title}</div>
               </div>
               <div style={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #b1b1b1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700
               }}>
                    {value ?? '—'}
               </div>
          </Card.Body>
     </Card>
);

const CompactPageButton = ({ children, active, onClick }) => (
     <Button size="sm" onClick={onClick}
          style={{
               minWidth: 25,
               height: 25,
               padding: '0 5px',
               borderRadius: 10,
               fontSize: 13,
               boxShadow: 'none',
               background: 'transparent',
               border: 'none',
               color: active ? '#4f46e5' : '#333',
               fontWeight: active ? 700 : 500
          }}>
          {children}
     </Button>
);

const HRBP_Dashboard = () => {
     console.log('🚀 HRBP_Dashboard component mounted');

     useAuth();
     const baseUrl = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'https://internalhrms.vdcapp.in/backend/api';
     const [loading, setLoading] = useState(true);
     const [error, setError] = useState(null);
     const [summary, setSummary] = useState({ total: 0, present: 0, on_leave: 0, absent: 0 });
     const [doctors, setDoctors] = useState([]);
     const [filter, setFilter] = useState('all');
     const [q, setQ] = useState('');
     const [page, setPage] = useState(1);
     const [pageSize, setPageSize] = useState(20);
     const [selectedDate, setSelectedDate] = useState(() => {
          const today = new Date();
          const yyyy = today.getFullYear();
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const dd = String(today.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
     });

     const [empType, setEmpType] = useState('DOCTOR');

     console.log('🔹 HRBP_Dashboard state:', { loading, error, baseUrl, empType, selectedDate });

     useEffect(() => {
          let mounted = true;
          const fetchData = async () => {
               console.log('🔄 HRBP_Dashboard: Starting data fetch...');
               setLoading(true);
               setError(null);
               try {
                    let url = `${baseUrl}/doctorSummary`;
                    const params = new URLSearchParams();
                    if (selectedDate) params.append('date', selectedDate);
                    if (empType) params.append('emp_type', empType);
                    if (params.toString()) url += `?${params.toString()}`;

                    console.log('📡 Fetching from:', url);

                    const res = await fetch(url, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${sessionStorage.getItem('token')}`,
                         },
                    });

                    console.log('📥 Response status:', res.status, res.statusText);

                    if (!res.ok) {
                         throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                    }

                    const j = await res.json();
                    console.log('✅ API Response:', j);

                    if (!mounted) return;
                    if (j && j.status && j.data) {
                         const s = j.data.summary || {};
                         setSummary({
                              total: s.total ?? 0,
                              present: s.present ?? 0,
                              on_leave: s.on_leave ?? 0,
                              absent: s.absent ?? 0,
                         });
                         setDoctors(Array.isArray(j.data.details) ? j.data.details : []);
                         console.log('✅ Data loaded:', { summary: s, doctorsCount: j.data.details?.length });
                    } else {
                         console.warn('⚠️ Invalid API response structure:', j);
                         setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                         setDoctors([]);
                    }
               } catch (err) {
                    console.error('❌ Doctor summary fetch error:', err);
                    if (mounted) {
                         setError(err.message);
                         setSummary({ total: 0, present: 0, on_leave: 0, absent: 0 });
                         setDoctors([]);
                    }
               } finally {
                    if (mounted) {
                         setLoading(false);
                         console.log('✅ Fetch complete, loading set to false');
                    }
               }
          };
          fetchData();
          return () => {
               console.log('🧹 HRBP_Dashboard cleanup');
               mounted = false;
          };
     }, [baseUrl, selectedDate, empType]);

     const filtered = useMemo(() => {
          const qlow = (q || '').trim().toLowerCase();
          return doctors.filter(d => {
               if (!d) return false;
               if (filter !== 'all' && (d.status || '').toLowerCase() !== filter) return false;
               if (!qlow) return true;
               return (
                    (d.employee_name || '').toLowerCase().includes(qlow) ||
                    (d.employee_code || '').toLowerCase().includes(qlow)
               );
          });
     }, [doctors, filter, q]);

     const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
     const pageClamped = Math.min(Math.max(1, page), totalPages);
     useEffect(() => { setPage(1); }, [filter, q, pageSize]);

     const pageItems = filtered.slice((pageClamped - 1) * pageSize, pageClamped * pageSize);

     const maxBtns = 10;
     let start = 1;
     let end = totalPages;
     if (totalPages > maxBtns) {
          start = Math.max(1, pageClamped - Math.floor(maxBtns / 2));
          end = start + maxBtns - 1;
          if (end > totalPages) {
               end = totalPages;
               start = Math.max(1, end - maxBtns + 1);
          }
     }

     console.log('🎨 HRBP_Dashboard rendering:', { loading, error, filteredCount: filtered.length });

     // ✅ Show loading spinner
     if (loading) {
          console.log('⏳ Showing loading spinner');
          return (
               <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: '20px' }}>
                    <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
                         <span className="visually-hidden">Loading dashboard...</span>
                    </Spinner>
                    <p style={{ fontSize: '18px', color: '#666' }}>Loading HR Dashboard...</p>
               </div>
          );
     }

     // ✅ Show error message
     if (error) {
          console.log('❌ Showing error:', error);
          return (
               <div style={{ padding: 24, maxWidth: 600, margin: '40px auto' }}>
                    <Alert variant="danger">
                         <Alert.Heading>⚠️ Dashboard Error</Alert.Heading>
                         <p>Failed to load HR Dashboard</p>
                         <hr />
                         <p className="mb-0" style={{ fontSize: '14px', fontFamily: 'monospace' }}>{error}</p>
                         <Button variant="outline-danger" className="mt-3" onClick={() => window.location.reload()}>
                              Retry
                         </Button>
                    </Alert>
               </div>
          );
     }

     console.log('✅ Rendering main dashboard content');

     return (
          <div style={{ padding: 12, minHeight: '100vh', fontSize: 14, background: '#f8f9fa' }}>
               <div style={{ margin: '0 auto', maxWidth: 1400 }}>
                    
                    <Row className="mb-2">
                         <Col xs={3}>
                              <Form.Select
                                   value={empType}
                                   onChange={e => setEmpType(e.target.value)}
                                   style={{ maxWidth: 250 }}
                              >
                                   <option value="DOCTOR">DOCTORS</option>
                                   <option value="CONTRACTUAL_EMPLOYEE">CONTRACTUAL EMPLOYEE</option>
                                   <option value="POOJARI">POOJARI</option>
                              </Form.Select>
                         </Col>
                         <Col xs={9}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                                   <StatCard
                                        title={`Total ${empType === 'DOCTOR' ? 'Doctors' : empType === 'CONTRACTUAL_EMPLOYEE' ? 'Employees' : 'Poojaris'}`}
                                        value={summary.total}
                                        color="#e8f0fe"
                                        active={filter === 'all'}
                                        onClick={() => setFilter('all')}
                                   />
                                   <StatCard
                                        title="Present"
                                        value={summary.present}
                                        color="#e9f7ef"
                                        active={filter === 'present'}
                                        onClick={() => setFilter('present')}
                                   />
                                   <StatCard
                                        title="Absent"
                                        value={summary.absent}
                                        color="#fff7e6"
                                        active={filter === 'absent'}
                                        onClick={() => setFilter('absent')}
                                   />
                                   <StatCard
                                        title="On Leave"
                                        value={summary.on_leave}
                                        color="#fff1f0"
                                        active={filter === 'on_leave'}
                                        onClick={() => setFilter('on_leave')}
                                   />
                              </div>
                         </Col>
                    </Row>

                    <Row className="mb-2" style={{ alignItems: 'center' }}>
                         <Col md={3}>
                              <Form.Control
                                   type="date"
                                   value={selectedDate}
                                   onChange={e => setSelectedDate(e.target.value)}
                                   size="sm"
                              />
                         </Col>
                         <Col md={5}>
                              <Form.Control placeholder="Search by name or code" value={q} onChange={e => setQ(e.target.value)} size="sm" />
                         </Col>
                         <Col md={4} className="text-end">
                              <Form.Select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: 120, display: 'inline-block' }} size="sm">
                                   <option value={10}>10 / page</option>
                                   <option value={20}>20 / page</option>
                                   <option value={50}>50 / page</option>
                              </Form.Select>
                         </Col>
                    </Row>

                    <Card className="mb-2" style={{ borderRadius: 8 }}>
                         <Card.Body style={{ padding: 0 }}>
                              <Table hover responsive size="sm" className="mb-0">
                                   <thead>
                                        <tr style={{ fontSize: 13 }}>
                                             <th style={{ width: 40 }}>#</th>
                                             <th style={{ width: 120 }}>Code</th>
                                             <th>Name</th>
                                             <th style={{ width: 200 }}>Shift</th>
                                             <th style={{ width: 200 }}>Punch In</th>
                                             <th style={{ width: 200 }}>Punch Out</th>
                                             <th style={{ width: 100 }}>Status</th>
                                        </tr>
                                   </thead>
                                   <tbody>
                                        {pageItems.length === 0 ? (
                                             <tr><td colSpan="7" className="text-center py-3">No records found</td></tr>
                                        ) : (
                                             pageItems.map((d, idx) => (
                                                  <tr key={d.emp_id || d.employee_code || idx}>
                                                       <td style={{ verticalAlign: 'middle' }}>{(pageClamped - 1) * pageSize + idx + 1}</td>
                                                       <td style={{ verticalAlign: 'middle' }}>{d.employee_code}</td>
                                                       <td style={{ verticalAlign: 'middle' }}>{d.employee_name}</td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            {d.shift_details ? (
                                                                 <div style={{ fontSize: 12 }}>
                                                                      <div style={{ fontWeight: 600 }}>{d.shift_details.shift_name}</div>
                                                                      <div style={{ color: '#666', fontSize: 11 }}>
                                                                           {d.shift_details.in_time?.substring(0, 5)} - {d.shift_details.out_time?.substring(0, 5)}
                                                                      </div>
                                                                 </div>
                                                            ) : (
                                                                 <span style={{ color: '#999' }}>-</span>
                                                            )}
                                                       </td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            {d.punch_in ? new Date(d.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                       </td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            {d.punch_in && d.punch_out && d.punch_in === d.punch_out && selectedDate === new Date().toISOString().split('T')[0]
                                                                 ? <span style={{ color: '#28a745', fontWeight: 600, fontSize: 11 }}>On Duty</span>
                                                                 : d.punch_out
                                                                      ? new Date(d.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                                      : '-'}
                                                       </td>
                                                       <td style={{ verticalAlign: 'middle' }}>
                                                            <span className={`badge ${d.status === 'present' ? 'bg-success' : d.status === 'absent' ? 'bg-danger' : 'bg-secondary'}`} style={{ textTransform: 'capitalize', fontSize: 12, padding: '4px 8px' }}>
                                                                 {d.status || '—'}
                                                            </span>
                                                       </td>
                                                  </tr>
                                             ))
                                        )}
                                   </tbody>
                              </Table>
                         </Card.Body>
                    </Card>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8 }}>
                         <div style={{ fontSize: 13, color: '#555' }}>
                              Showing {filtered.length === 0 ? 0 : (pageClamped - 1) * pageSize + 1} - {Math.min(pageClamped * pageSize, filtered.length)} of {filtered.length} Records
                         </div>

                         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ display: 'flex', gap: 6 }}>
                                   {Array.from({ length: end - start + 1 }).map((_, i) => {
                                        const p = start + i;
                                        return <CompactPageButton key={p} active={p === pageClamped} onClick={() => setPage(p)}>{p}</CompactPageButton>;
                                   })}
                              </div>
                         </div>
                    </div>

               </div>
          </div>
     );
};

export default HRBP_Dashboard;